import {
  CircleEmptyAlertIcon,
  Divider,
  Menu,
  Skeleton,
} from "@codat/orchard-ui";
import { useGetAccountsSummary } from "api/public";
import classNames from "classnames";
import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";

import styles from "./Sidebar.module.scss";
import SidebarLinkWrapper from "./SidebarLinkWrapper";

export interface SidebarItem {
  name: string;
  route: string | null;
  href?: string;
  indicator?: React.ReactNode;
}

export interface SidebarGroup {
  groupName?: string;
  indicator?: React.ReactNode;
  items: SidebarItem[];
  hasDivider?: boolean;
}

export interface SidebarProps {
  sidebarGroups: SidebarGroup[];
  isLoading?: boolean;
  className?: string;
}

const Sidebar = ({ sidebarGroups, isLoading, className }: SidebarProps) => {
  const location = useLocation();
  const { companyId } = useParams<{ companyId: string | undefined }>();

  const [showSubmenu, setShowSubmenu] = useState<boolean[]>(
    getSidebarSubmenuStates(sidebarGroups, location.pathname)
  );

  useEffect(() => {
    setShowSubmenu(getSidebarSubmenuStates(sidebarGroups, location.pathname));
  }, [sidebarGroups, location.pathname]);

  if (isLoading) {
    return (
      <div
        data-testid="sidebar"
        className={classNames(styles.sidebarWrapper, className)}
        aria-busy={true}
      >
        <div className={styles.sidebar}>
          {sidebarGroups.map((group, index) => (
            <div
              key={index}
              className={styles.group}
              data-testid={`sidebar-group-skeleton-${index}`}
            >
              <Skeleton
                width="6rem"
                height="1.25rem"
                className={classNames(styles.groupName, styles.skeleton)}
              />
              {(showSubmenu[index] || !group.groupName) &&
                group.items.map((_, index) => (
                  <div
                    key={index}
                    className={styles.item}
                    data-testid={`sidebar-item-skeleton-${index}`}
                  >
                    <Skeleton width="10rem" height="1.5rem" />
                  </div>
                ))}
            </div>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div
      data-testid="sidebar"
      className={classNames(styles.sidebarWrapper, className)}
      aria-busy={false}
    >
      <div className={styles.sidebar}>
        <Menu.List>
          {sidebarGroups.map((group, index) => {
            const showAssessSubmenu =
              group?.groupName === "Assess" && !!companyId;
            return showAssessSubmenu ? (
              <AssessSubmenu
                group={group}
                index={index}
                showSubmenu={showSubmenu}
                setShowSubmenu={setShowSubmenu}
                companyId={companyId}
                TabItem={AssessMenuItem}
              />
            ) : (
              <Submenu
                group={group}
                index={index}
                showSubmenu={showSubmenu}
                setShowSubmenu={setShowSubmenu}
              />
            );
          })}
        </Menu.List>
      </div>
    </div>
  );
};

interface SidebarItemWithWrapperProps {
  hasParent: boolean;
  parentWrapper: (item: React.ReactNode) => void;
  children: React.ReactNode;
}

const SidebarItemWithWrapper = ({
  hasParent,
  parentWrapper,
  children,
}: SidebarItemWithWrapperProps): JSX.Element => {
  return <>{hasParent ? parentWrapper(children) : children}</>;
};

const getSidebarSubmenuStates = (
  sidebarGroups: SidebarGroup[],
  currentLocation: string
) => {
  return sidebarGroups.map((submenu) =>
    submenu.items.some(
      (item) => item?.route && currentLocation.includes(item.route)
    )
      ? true
      : false
  );
};

interface SubmenuProps {
  group: SidebarGroup;
  index: number;
  showSubmenu: boolean[];
  setShowSubmenu: (value: React.SetStateAction<boolean[]>) => void;
  isAssess?: boolean;
  displayCategorizeWarning?: boolean;
  TabItem?: React.FC<MenuItemProps>;
}

const Submenu: React.FC<SubmenuProps> = ({
  group,
  index,
  showSubmenu,
  setShowSubmenu,
  TabItem = MenuItem,
}) => {
  return (
    <React.Fragment key={`${group.groupName}-${index}`}>
      {group.groupName && (
        <Menu.Item
          id={`sidebar-group-item-${index}`}
          key={`sidebar-group-item-${index}`}
          onClick={() => {
            setShowSubmenu(
              showSubmenu.map((e, i) => {
                return i == index ? !e : false;
              })
            );
          }}
          data-testid={`sidebar-group-${index}`}
          label={
            <>
              {group.groupName}
              {group?.indicator && (
                <div
                  className={styles.indicator}
                  data-testid={`sidebar-group-indicator-${index}`}
                >
                  {group.indicator}
                </div>
              )}
            </>
          }
          hasSubmenu
          showSubmenu={showSubmenu[index]}
        />
      )}
      {(showSubmenu[index] || !group.groupName) && (
        <SidebarItemWithWrapper
          hasParent={!!group.groupName}
          parentWrapper={(children) => <Menu.List>{children}</Menu.List>}
        >
          <>
            {group.items.map((item, i) => (
              <TabItem item={item} key={`${item}-${i}`} index={i} />
            ))}
          </>
        </SidebarItemWithWrapper>
      )}
      {group.hasDivider && <Divider className={styles.divider} />}
    </React.Fragment>
  );
};

const AssessSubmenu: React.FC<SubmenuProps & { companyId: string }> = ({
  group,
  index,
  showSubmenu,
  setShowSubmenu,
  companyId,
}) => {
  const categorizeAccountsThreshold = 0.03;

  const [{ data }] = useGetAccountsSummary(companyId);
  const displayCategorizeWarning =
    data?.accounts[0]?.priority === undefined
      ? false
      : data?.accounts[0]?.priority >= categorizeAccountsThreshold;

  return (
    <Submenu
      group={group}
      index={index}
      showSubmenu={showSubmenu}
      setShowSubmenu={setShowSubmenu}
      displayCategorizeWarning
      TabItem={displayCategorizeWarning ? AssessMenuItem : MenuItem}
    />
  );
};

interface MenuItemProps {
  item: SidebarItem;
  index: number;
}
const AssessMenuItem: React.FC<MenuItemProps> = ({ item, index }) => {
  const isCategorizeAccounts = item.name === "Categorize accounts";
  const isSelected = location.pathname === item.route;
  const displayCategoriseWarning = !isSelected && isCategorizeAccounts;

  return (
    <Menu.Item
      id={`sidebar-item-${index}`}
      onClick={undefined}
      key={`sidebar-item-${index}`}
      selected={isSelected}
      icon={displayCategoriseWarning ? <CircleEmptyAlertIcon /> : null}
      subtext={
        displayCategoriseWarning
          ? "Categorize accounts for more accurate data"
          : undefined
      }
      className={classNames(styles.menuItem, {
        [styles.categoriseWarning]: displayCategoriseWarning,
      })}
      label={
        <SidebarLinkWrapper item={item} index={index}>
          {item.name}
          {item?.indicator && (
            <div
              className={styles.indicator}
              data-testid={`sidebar-item-indicator-${index}`}
            >
              {item.indicator}
            </div>
          )}
        </SidebarLinkWrapper>
      }
    />
  );
};

const MenuItem: React.FC<MenuItemProps> = ({ item, index }) => (
  <Menu.Item
    id={`sidebar-item-${index}`}
    onClick={undefined}
    key={`sidebar-item-${index}`}
    selected={location.pathname === item.route}
    className={styles.menuItem}
    label={
      <SidebarLinkWrapper item={item} index={index}>
        {item.name}
        {item?.indicator && (
          <div
            className={styles.indicator}
            data-testid={`sidebar-item-indicator-${index}`}
          >
            {item.indicator}
          </div>
        )}
      </SidebarLinkWrapper>
    }
  />
);

export default Sidebar;
